import React, { type ReactNode } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

interface CalendarTemplateProps {
  header?: ReactNode;
  sidebar?: ReactNode;
  main: ReactNode;
  /** Persistent right-hand panel (e.g. booking requests) on desktop. */
  rightPanel?: ReactNode;
  isSidebarOpen?: boolean;
  onSidebarClose?: () => void;
}

/**
 * CalendarTemplate - A flexible layout template for calendar views
 * Provides a composition-based layout with header, sidebar, and main content areas
 * Responsive: Drawer on mobile, fixed sidebar on desktop
 */
export const CalendarTemplate: React.FC<CalendarTemplateProps> = ({
  header,
  sidebar,
  main,
  rightPanel,
  isSidebarOpen = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarWidth = 256;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#F4F1F8', // TODO: Replace with theme color
      }}
    >
      {/* Header Section - Full width */}
      {header && (
        <Box
          sx={{
            flexShrink: 0,
            width: '100%',
            zIndex: 100,
          }}
        >
          {header}
        </Box>
      )}

      {/* Main Content Area with Sidebar */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          overflow: 'hidden',
          width: '100%',
          position: 'relative',
        }}
      >
        {/* Sidebar - slides in/out on mobile, fixed on desktop */}
        {sidebar && (
          <Box
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              overflow: 'auto',
              position: 'relative',
              opacity: isMobile ? (isSidebarOpen ? 1 : 0) : 1,
              marginLeft: isMobile && !isSidebarOpen ? `-${sidebarWidth}px` : 0,
              height: '100%',
              transition: 'opacity 0.2s ease-in-out, margin-left 0.2s ease-in-out',
            }}
          >
            {sidebar}
          </Box>
        )}

        {/* Main Content - Flexible width */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            mx: 2,
            mb: 2,
            mt: 0,
            borderRadius: 3,
            backgroundColor: '#F8F8F8', // TODO: Replace with theme color
          }}
        >
          {main}
        </Box>

        {/* Right panel (booking requests) — persistent beside the calendar
            on desktop; mobile presentation is the caller's concern. */}
        {rightPanel && !isMobile && (
          <Box
            sx={{
              width: 400,
              flexShrink: 0,
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              mr: 2,
              mb: 2,
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            {rightPanel}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CalendarTemplate;
